<template>
  <!--
    `class` e `style` que a tela passar ficam NA MOLDURA (`mb-3`, `w-auto`, largura), porque
    é ela que ocupa espaço no layout. O que muda a aparência do CAMPO (`form-select-sm`,
    `is-invalid`) vai por `classeCampo`/`estiloCampo` — um `form-select-sm` na moldura não
    diminui combo nenhum, e foi assim que os combos das grades voltaram ao tamanho normal na
    primeira leva da migração.
  -->
  <div class="dropdown-consulte" :class="$attrs.class" :style="$attrs.style">
    <!--
      Rótulo do próprio componente: visível por padrão, e apenas para leitor de tela quando a
      tela já mostra o rótulo por conta própria (`rotuloVisivel = false`). Um combo sem rótulo
      é anunciado como "caixa de combinação" sem dizer do que se trata.

      Sem `label` não se desenha rótulo nenhum: ou a tela já tem o dela (caso do `id` externo,
      usado na migração dos `<select>` crus), ou o campo recebe nome por `aria-label`.
    -->
    <label
      v-if="label"
      :for="idCampo"
      :class="rotuloVisivel ? 'form-label' : 'apenas-leitor-tela'"
    >
      {{ label }}<span v-if="required" aria-hidden="true"> *</span>
      <span v-if="required" class="apenas-leitor-tela"> (obrigatório)</span>
    </label>

    <!--
      COM BUSCA: o mesmo combo, com autocomplete, quando a lista é grande demais para rolar.

      É o MESMO componente de propósito. A tela não escolhe o widget — ela declara o combo, e
      quem decide pelo tamanho da lista é aqui. Assim o sistema inteiro tem um comportamento só,
      e mudar o critério é mudar uma linha em vez de varrer 268 telas.

      `append-to-body` porque o painel abre dentro de grade e de modal, onde o `overflow` do
      contêiner cortaria a lista pela metade.

      A opção vazia (`placeholder`) é um ITEM da lista aqui também, como no `<select>` nativo.
      Sem isso, "Desconsiderar escola (toda a rede)" ou "Todos os ensinos" só existiam como
      texto do campo vazio: com a escola da barra já escolhida, a opção sumia, e a única saída
      era um "x" que ninguém associa a "a rede inteira". O mesmo combo aparecia num contrato
      (até 12 escolas, nativo) e não aparecia em outro (mais de 12, com busca).
    -->
    <MultiSelect
      v-if="usarBusca"
      :id="idCampo"
      mode="single"
      :searchable="true"
      :append-to-body="true"
      :options="opcoesDaBusca"
      :model-value="valorDaBusca"
      :disabled="disabled"
      :can-clear="!!placeholder && valorDaBusca !== opcaoVazia"
      :can-deselect="false"
      :placeholder="placeholder || undefined"
      :aria="ariaDaBusca"
      :class="classeCampo"
      :style="estiloCampo"
      no-options-text="Nenhuma opção disponível"
      no-results-text="Nenhuma opção encontrada"
      @change="aoEscolherNaBusca"
      @keydown="aoTeclarNaBusca"
    >
      <template #option="{ option }">
        <span :class="{ 'opcao-combo-destacada': option.destacada }">{{ option.label }}</span>
      </template>
    </MultiSelect>

    <!--
      O NOME do campo para o leitor de tela.

      Quando é pesquisável, o pacote põe `aria-labelledby` apontando para um texto próprio (o
      valor escolhido) no campo de busca — e `aria-labelledby` VENCE o `<label for>`, então sem
      isto o campo seria anunciado só pelo valor, sem dizer de que campo se trata. Passando o
      nosso id junto, o pacote compõe os dois: "valor escolhido, Nome do campo".
    -->
    <span v-if="usarBusca && nomeAcessivel" :id="idNomeAcessivel" class="apenas-leitor-tela">
      {{ nomeAcessivel }}
    </span>

    <!--
      Atributos soltos da tela (`aria-describedby`, `title`, `autocomplete`, `data-*`) caem NO
      CAMPO, e não no `<div>` de fora — um `aria-describedby` na moldura descreveria o lugar
      errado. `class`/`style` são a exceção, e ficam na moldura (ver o comentário do topo).
      Os eventos declarados em `emits` não entram no `$attrs`, então não há risco de disparo
      em dobro.

      `v-if="!usarBusca"`, e NÃO `v-else`: o `v-else` se prende ao `v-if` IMEDIATAMENTE
      anterior, que é o `<span>` do nome acessível — não o `MultiSelect`. Com busca ligada e sem
      nome acessível, o `<span>` não saía e o `<select>` saía JUNTO do combo com busca: dois
      campos para o mesmo valor (aconteceu nas Atividades Complementares da turma).
    -->
    <select
      v-if="!usarBusca"
      v-bind="atributosDoCampo"
      :id="idCampo"
      class="form-select"
      :class="classeCampo"
      :style="estiloCampo"
      :value="valorAtual"
      :disabled="disabled"
      :required="required"
      :aria-label="label ? undefined : ariaLabel || undefined"
      :aria-required="required ? 'true' : undefined"
      :aria-invalid="mensagemErro ? 'true' : undefined"
      :aria-describedby="mensagemErro ? idErro : undefined"
      @change="aoTrocar"
    >
      <!-- Opção vazia explícita: sem ela o primeiro item da lista aparece como se
           tivesse sido escolhido, e não há como voltar a "nada selecionado".

           O valor dela é `valorVazio` e não uma string fixa: em boa parte das telas "nenhum"
           é o código ZERO ("Todos os ensinos", "Sem grupo"), e devolver `null` no lugar dele
           faz a tela comparar com `0` e não achar, ou mandar `null` para uma API que espera
           `int`. Nada disso aparece na tela — só no resultado. -->
      <option v-if="placeholder" :value="valorVazio">{{ placeholder }}</option>

      <!--
        DUAS FORMAS de alimentar a lista, e as duas existem por necessidade real:

        `array` + `rowLabel`/`rowValue` — a lista que vem do banco. É a preferida: só com o
        array em mãos o componente consegue devolver o valor no TIPO ORIGINAL (ver `aoTrocar`).

        O SLOT com `<option>` soltos — a lista curta e fixa ("Masculino/Feminino", "Sim/Não"),
        a que tem `v-if` por opção, ou a que mistura item fixo com item da lista. Sem ele,
        migrar os selects estáticos do sistema exigiria criar um array em `data()` para cada
        um; com ele, a migração é trocar a tag. O valor emitido aí é o mesmo que o `v-model` de
        um `<select>` nativo devolveria: o da `:value` no tipo em que foi escrita, ou o texto
        do `value` literal (ver `valorEscolhido`).
      -->
      <slot>
        <option
          v-for="item in array"
          :key="String(item[rowValue])"
          :value="item[rowValue]"
          :class="{ 'opcao-combo-destacada': rowDestaque && item[rowDestaque] }"
        >
          {{ item[rowLabel] }}
        </option>
      </slot>
    </select>

    <!-- Mensagem de erro ligada ao campo por `aria-describedby` (acima): sem o vínculo,
         o leitor de tela anuncia "inválido" sem dizer o motivo. -->
    <div v-if="mensagemErro" :id="idErro" class="invalid-feedback d-block">
      {{ mensagemErro }}
    </div>
  </div>
</template>

<script lang="ts">
  import { Fragment, defineComponent } from 'vue';
  import type { VNode } from 'vue';

  /**
   * Combo padrão do sistema, sobre `<select>` NATIVO.
   *
   * Por que nativo: em celular o `<select>` abre o seletor do próprio sistema
   * operacional (rolagem por toque, busca, tamanho de fonte do usuário), e no desktop já
   * vem com teclado completo — setas, Home/End, busca por digitação, Enter, Esc — sem
   * uma linha de JavaScript. Nenhuma lista customizada iguala isso. Use este componente
   * sempre que a escolha for de uma lista simples; recorra ao `@vueform/multiselect`
   * apenas quando precisar de busca dentro da lista, seleção múltipla ou marcadores.
   *
   * O que foi corrigido nesta reescrita (o componente estava inutilizável, e por isso
   * sem nenhum uso no projeto):
   *
   *  - o `watch` do `array` ligava `reload = true` e NUNCA voltava para `false` (a linha
   *    que o desligava estava comentada). Como o template era `v-if="!reload"`, o combo
   *    desaparecia da tela para sempre na primeira vez que a lista mudasse. O `reload`
   *    saiu: trocar `:key` já bastaria, e nem isso é necessário — o Vue re-renderiza as
   *    `<option>` sozinho quando `array` muda;
   *  - `title="Single Select"` estava fixo no `<select>`: virava o nome acessível do
   *    campo (o leitor de tela anunciava "Single Select"), além de mostrar essa dica em
   *    inglês ao passar o mouse;
   *  - não havia como associar rótulo, marcar obrigatoriedade nem ligar mensagem de erro;
   *  - `:key="item"` usava o objeto como chave, o que impede o Vue de reaproveitar nós.
   */
  let sequencial = 0;

  /**
   * Valor interno da opção vazia no combo com busca. O pacote usa `null` para "nada escolhido",
   * então a opção vazia não pode ter `null` (nem o `valorVazio` da tela, que pode ser `null`)
   * como valor: ela seria confundida com o campo limpo. Nunca sai do componente.
   */
  const OPCAO_VAZIA = '__ddc_opcao_vazia__';

  /** Igualdade de valor de opção, como a do `v-model` do `<select>` (`"3"` casa com `3`). */
  const mesmoValor = (a: unknown, b: unknown): boolean =>
    a === b || (a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b));

  export default defineComponent({
    name: 'DropDownConsulte',
    inheritAttrs: false,
    props: {
      /** Valor escolhido. Aceita `v-model` (modelValue) ou a prop `value` do uso antigo. */
      modelValue: { type: [String, Number, null], default: undefined },
      value: { type: [String, Number, null], default: undefined },

      array: { type: Array as () => Record<string, any>[], default: () => [] },
      /**
       * Campos do item que viram texto e valor da opção. Obrigatórios quando a lista vem por
       * `array`; dispensáveis quando as `<option>` vêm pelo slot.
       */
      rowLabel: { type: String, default: '' },
      rowValue: { type: String, default: '' },

      /**
       * Campo do item que, verdadeiro, DESTACA a opção (verde e negrito, nos dois modos — ver
       * `.opcao-combo-destacada`). Só vale para a lista vinda do `array`. A cor não basta
       * sozinha: o texto da opção tem de dizer o porquê (ex.: "— já alocado"). No `<select>`
       * nativo do celular o sistema operacional ignora a cor; o texto continua lá.
       */
      rowDestaque: { type: String, default: '' },

      /**
       * Id do `<select>`, quando a TELA já desenha o próprio `<label for=...>`.
       *
       * <p>
       * Existe para a migração dos `<select>` crus do sistema: as telas têm o rótulo e o `for`
       * apontando para um id que elas mesmas escolheram. Sem esta prop, trocar a tag pelo
       * componente faria o `for` apontar para um id inexistente — o rótulo deixaria de nomear o
       * campo, e a troca PIORARIA a acessibilidade em vez de melhorá-la.
       * </p>
       *
       * <p>
       * Informado o `id`, o componente não desenha rótulo nenhum: o da tela é o legítimo, e dois
       * rótulos para o mesmo campo confundem o leitor de tela. Em tela nova prefira o `label` —
       * ele gera o par `for`/`id` sozinho e não há como esquecer.
       * </p>
       */
      id: { type: String, default: '' },

      /** Rótulo do campo. É o caminho preferido — gera o par `for`/`id`. */
      label: { type: String, default: '' },
      /** `false` quando a tela já desenha o rótulo: ele fica só para leitor de tela. */
      rotuloVisivel: { type: Boolean, default: true },
      /** Nome acessível quando não há `label` (evite: prefira sempre o `label`). */
      ariaLabel: { type: String, default: '' },

      /**
       * Modificadores do `v-model` (`.number`, `.trim`), entregues pelo Vue.
       *
       * <p>
       * Num `<select>` nativo o Vue aplica esses modificadores sozinho; num COMPONENTE, quem
       * aplica é o componente. Sem isto, migrar `v-model.number="codigoEnsino"` para cá faria o
       * modificador virar decoração silenciosa — e um `"3"` onde a tela compara com `3` não
       * quebra nada visível, só passa a dar falso.
       * </p>
       */
      modelModifiers: { type: Object as () => Record<string, boolean>, default: () => ({}) },

      /** Classe(s) do próprio `<select>` — `form-select-sm`, `is-invalid`, etc. */
      classeCampo: { type: [String, Array, Object], default: '' },
      /** Estilo do próprio `<select>`, para o que não couber numa classe. */
      estiloCampo: { type: [String, Object], default: '' },

      /**
       * Liga o autocomplete. `'auto'` (padrão) decide pelo TAMANHO da lista.
       *
       * <p>
       * Lista curta (Sim/Não, Turno, Sexo) continua no `<select>` nativo, que no celular abre o
       * seletor do sistema operacional — rolagem por toque, busca e tamanho de fonte do usuário,
       * de graça. Lista longa (escolas, municípios, servidores) ganha busca, onde rolar uma
       * lista de trezentos itens é o que não funciona.
       * </p>
       *
       * <p>
       * No automático a busca NÃO entra em dois casos, porque mudariam contrato de formulário:
       * campo `required` (a validação nativa do `<form>` depende de um controle nativo) e campo
       * que recebeu `onBlur` (o vee-validate valida no blur, e o painel de busca não emite
       * blur). A tela pode forçar com `busca` ou `:busca="false"`.
       * </p>
       */
      busca: { type: [Boolean, String], default: 'auto' },
      /** A partir de quantas opções o automático liga a busca. */
      limiteBusca: { type: Number, default: 12 },

      placeholder: { type: String, default: 'Selecione' },
      /**
       * O valor que a opção vazia (`placeholder`) devolve.
       *
       * <p>
       * `null` por padrão, que é o que "nada selecionado" significa num combo de código e o que
       * a API aceita num `int?`. Telas em que "nenhum" tem código próprio — `0` para "Todos os
       * ensinos", `-1` para "Não associado" — informam esse código aqui.
       * </p>
       */
      valorVazio: { type: [String, Number, null], default: null },
      disabled: { type: Boolean, default: false },
      required: { type: Boolean, default: false },
      /** Mensagem de validação. Preenchida, marca o campo como inválido. */
      mensagemErro: { type: String, default: '' },
    },
    emits: ['update:modelValue', 'change'],
    data() {
      sequencial += 1;
      return { sequencial, rotuloExterno: '' };
    },
    computed: {
      /** `modelValue` tem precedência; `value` mantém compatível o uso antigo. */
      valorAtual(): string | number | null | undefined {
        return this.modelValue !== undefined ? this.modelValue : this.value;
      },
      /** O id da TELA quando informado; senão um gerado por instância. */
      idCampo(): string {
        return this.id || `ddc-${this.sequencial}`;
      },
      idErro(): string {
        return `ddc-${this.sequencial}-erro`;
      },
      idNomeAcessivel(): string {
        return `ddc-${this.sequencial}-nome`;
      },

      /**
       * A lista, no formato do combo com busca — venha ela do `array` ou das `<option>` do slot.
       *
       * <p>
       * Ler o slot é o que permite ligar a busca em TODA tela sem reescrever nenhuma: as
       * `<option>` chegam aqui já renderizadas pelo pai, com o valor no tipo original e o texto
       * resolvido (`{{ a }} - {{ b }}` chega como um texto só).
       * </p>
       */
      opcoes(): { value: unknown; label: string; disabled?: boolean; destacada?: boolean }[] {
        const doSlot = this.opcoesDoSlot();

        if (doSlot.length) return doSlot;

        return (this.array || []).map((item) => ({
          value: item?.[this.rowValue],
          label: String(item?.[this.rowLabel] ?? ''),
          destacada: !!this.rowDestaque && !!item?.[this.rowDestaque],
        }));
      },

      opcaoVazia(): string {
        return OPCAO_VAZIA;
      },

      /** A lista do combo com busca: a opção vazia primeiro, como no `<select>` nativo. */
      opcoesDaBusca(): { value: unknown; label: string; disabled?: boolean }[] {
        if (!this.placeholder) return this.opcoes;

        return [{ value: OPCAO_VAZIA, label: this.placeholder }, ...this.opcoes];
      },

      /**
       * O valor mostrado no combo com busca. Valor que não está na lista (o `valorVazio`, `null`,
       * ou um `0` que a tela usa como "todos" sem tê-lo declarado) mostra a opção vazia — é o que
       * o `<select>` nativo faz quando nenhuma opção casa: exibe a primeira.
       */
      valorDaBusca(): unknown {
        if (!this.placeholder) return this.valorAtual;

        const naLista = this.opcoes.some((opcao) => mesmoValor(opcao.value, this.valorAtual));

        return naLista ? this.valorAtual : OPCAO_VAZIA;
      },

      /** Ver a prop `busca`. */
      usarBusca(): boolean {
        if (this.busca === true) return true;
        if (this.busca === false) return false;

        if (this.required) return false;
        if (this.$attrs.onBlur) return false;

        return this.opcoes.length > this.limiteBusca;
      },

      /** O nome do campo: o rótulo do componente, o `ariaLabel`, ou o `<label>` da tela. */
      nomeAcessivel(): string {
        return this.label || this.ariaLabel || this.rotuloExterno;
      },

      /** ARIA do combo com busca — o pacote compõe isto com o que ele mesmo gera. */
      ariaDaBusca(): Record<string, string> {
        const aria: Record<string, string> = {};

        if (this.nomeAcessivel) aria['aria-labelledby'] = this.idNomeAcessivel;

        const descricoes = [
          this.$attrs['aria-describedby'] as string | undefined,
          this.mensagemErro ? this.idErro : '',
        ].filter(Boolean);

        if (descricoes.length) aria['aria-describedby'] = descricoes.join(' ');
        if (this.mensagemErro) aria['aria-invalid'] = 'true';

        return aria;
      },
      /** Tudo o que a tela passou a mais, menos `class`/`style`, que ficam na moldura. */
      atributosDoCampo(): Record<string, unknown> {
        const resto: Record<string, unknown> = { ...this.$attrs };

        delete resto.class;
        delete resto.style;

        return resto;
      },
    },
    created() {
      // Com `id` informado, o rótulo é da TELA (um `<label for>` que o componente não enxerga) e
      // o aviso seria falso positivo em todo ponto migrado. Quem continua cobrando esse caso é a
      // auditoria `campoSemRotulo` do `npm run a11y`, que lê o HTML inteiro e vê o par.
      if (!this.label && !this.ariaLabel && !this.id) {
        // Falha silenciosa de acessibilidade é a pior: sem rótulo o campo é anunciado
        // apenas como "caixa de combinação". O aviso aparece no console em
        // desenvolvimento para o problema não passar batido na revisão.
        console.warn(
          '[DropDownConsulte] combo sem rótulo: informe `label` (preferido), `ariaLabel`, ou '
            + '`id` quando a própria tela desenha o <label for=...>.',
        );
      }
    },
    mounted() {
      /*
       * O rótulo quando quem o desenha é a TELA (prop `id`): o componente não o enxerga, mas
       * precisa do TEXTO dele para nomear o combo com busca. Lido uma vez, do `<label for>` que
       * aponta para este campo — o mesmo vínculo que o `<select>` nativo usaria sozinho.
       */
      if (!this.label && !this.ariaLabel && this.idCampo) {
        const rotulo = document.querySelector(`label[for="${this.idCampo}"]`);

        this.rotuloExterno = (rotulo?.textContent || '').replace(/\s+/g, ' ').trim();
      }
    },
    methods: {
      /**
       * A lista escrita como `<option>` no slot, lida dos nós já renderizados pelo pai.
       *
       * <p>
       * `v-for` chega como Fragment e `v-if` como comentário — por isso o achatamento recursivo
       * e o filtro por `type === 'option'`. O valor sai do `_value` que o Vue guarda na `:value`
       * (o mesmo lugar de onde o `v-model` nativo tira o dele), e o texto já vem interpolado.
       * </p>
       */
      opcoesDoSlot(): { value: unknown; label: string; disabled?: boolean }[] {
        const nos = this.$slots.default?.();

        if (!nos?.length) return [];

        const saida: { value: unknown; label: string; disabled?: boolean }[] = [];

        const achatar = (lista: unknown[]): void => {
          for (const item of lista) {
            const no = item as VNode;

            if (!no || typeof no !== 'object') continue;

            if (no.type === Fragment || (no.type !== 'option' && Array.isArray(no.children))) {
              achatar(no.children as unknown[]);
              continue;
            }

            if (no.type !== 'option') continue;

            const texto = this.textoDoNo(no);
            const props = (no.props || {}) as Record<string, unknown>;

            saida.push({
              value: 'value' in props ? props.value : texto,
              label: texto,
              disabled: props.disabled === '' || props.disabled === true,
            });
          }
        };

        achatar(nos);

        return saida;
      },

      /** O texto de uma `<option>`, já com as interpolações resolvidas. */
      textoDoNo(no: VNode): string {
        const filhos = no.children;

        if (typeof filhos === 'string') return filhos.replace(/\s+/g, ' ').trim();

        if (Array.isArray(filhos)) {
          return filhos
            .map((filho) => {
              if (typeof filho === 'string') return filho;

              const interno = (filho as VNode)?.children;

              return typeof interno === 'string' ? interno : '';
            })
            .join('')
            .replace(/\s+/g, ' ')
            .trim();
        }

        return '';
      },

      /**
       * O combo com busca devolve `null` ao limpar; aqui isso vira o `valorVazio` da tela, e a
       * emissão sai na mesma ordem do caminho nativo — `update:modelValue` ANTES de `change`.
       *
       * <p>
       * A ordem importa: o `@vueform/multiselect` emite o `change` dele ANTES de atualizar o
       * `v-model`, e um `@change` de tela que lesse o modelo pegaria o valor anterior. Como a
       * emissão para fora é feita aqui, as telas não veem essa diferença.
       * </p>
       */
      /**
       * TAB confirma a opção em destaque, como o Enter já fazia.
       *
       * <p>
       * O `<select>` nativo que este componente substitui escolhe com o teclado e sai do campo
       * com Tab levando a escolha junto; o pacote trata Tab só quando há criação de opção, então
       * sem isto quem digitasse e saísse com Tab perderia o que estava em destaque — e teclado é
       * requisito, não conforto (WCAG 2.1.1).
       * </p>
       *
       * <p>
       * O evento NÃO é cancelado de propósito: Tab confirma E leva o foco ao próximo campo, que
       * é o que quem preenche formulário com teclado espera. `Shift+Tab` fica de fora — voltar
       * não é confirmar.
       * </p>
       */
      aoTeclarNaBusca(evento: KeyboardEvent, combo: Record<string, unknown>) {
        if (evento.key !== 'Tab') return;
        if (evento.shiftKey || evento.altKey || evento.ctrlKey || evento.metaKey) return;

        // Sem lista aberta ou sem nada em destaque não há o que confirmar.
        if (!combo?.isOpen || !combo?.pointer) return;

        (combo.selectPointer as () => void)();
      },

      aoEscolherNaBusca(valor: unknown) {
        const vazio = valor === null || valor === undefined || valor === OPCAO_VAZIA;
        const escolhido = vazio ? this.valorVazio : valor;
        const final = this.comModificadores(escolhido as string | number | boolean | null);

        this.$emit('update:modelValue', final);
        this.$emit('change', final);
      },

      /**
       * Emite o valor escolhido NO TIPO ORIGINAL do item, e não o texto do `<select>`.
       *
       * <p>
       * O DOM só guarda string: `evento.target.value` de uma opção cujo valor é o número 3 volta
       * como `"3"`. Um `<select>` nativo com `v-model` não tem esse problema — o Vue guarda o
       * valor real da `:value` e devolve o número. Emitir a string fazia este componente se
       * comportar DIFERENTE do elemento que ele substitui: `codigoEscola === 3` passava a dar
       * falso, e um `"3"` enviado onde a API espera `int` derruba a requisição inteira na
       * desserialização (foi o que aconteceu com o campo Sexo da rematrícula).
       * </p>
       *
       * <p>
       * Com `array` em mãos, o item escolhido é reencontrado por comparação de texto e o valor
       * devolvido é o dele. Sem `array` (opções vindas pelo slot), não há tipo a recuperar e a
       * string é o valor legítimo — igual ao que o `<select>` cru entregaria.
       * </p>
       */
      aoTrocar(evento: Event) {
        const alvo = evento.target as HTMLSelectElement;
        const valor = this.comModificadores(this.valorEscolhido(alvo));

        this.$emit('update:modelValue', valor);
        this.$emit('change', valor);
      },

      /**
       * Aplica `.number`/`.trim` do `v-model`, como o Vue faz no `<select>` nativo.
       *
       * <p>
       * `.number` segue a regra do próprio Vue: converte quando dá, e devolve o valor original
       * quando não dá. Assim `null` continua `null` (e não vira `0`, como `Number(null)` faria)
       * e um código alfanumérico não vira `NaN`.
       * </p>
       */
      comModificadores(valor: string | number | boolean | null) {
        let resultado = valor;

        if (this.modelModifiers.trim && typeof resultado === 'string') {
          resultado = resultado.trim();
        }

        if (this.modelModifiers.number && resultado !== null && resultado !== '') {
          const numero = parseFloat(String(resultado));

          if (!Number.isNaN(numero)) resultado = numero;
        }

        return resultado;
      },

      /**
       * O valor da opção escolhida, no tipo em que foi declarado — por qualquer um dos dois
       * caminhos de alimentação da lista.
       *
       * <p>
       * <b>Opção vinda do slot</b>: quando o template escreve `:value="1"`, o Vue guarda o valor
       * real no elemento (`_value`) porque o DOM só aceita string. É exatamente daí que o
       * `v-model` do `<select>` nativo tira o número — ler o mesmo lugar é o que faz este
       * componente se comportar como o elemento que substitui. Com `value="1"` literal não há
       * `_value`, e a string é o valor legítimo, igual ao nativo.
       * </p>
       *
       * <p>
       * <b>Opção vinda do `array`</b>: o item é reencontrado pelo texto e devolve-se o campo dele.
       * </p>
       */
      valorEscolhido(alvo: HTMLSelectElement): string | number | boolean | null {
        const opcao = alvo.selectedOptions?.[0] as (HTMLOptionElement & { _value?: unknown }) | undefined;

        if (opcao && '_value' in opcao) return opcao._value as string | number | boolean | null;

        return this.valorOriginal(alvo.value);
      },

      /** O valor do item cujo `rowValue` corresponde ao texto escolhido. */
      valorOriginal(texto: string): string | number | null {
        /*
         * A opção vazia devolve NULL, e não string vazia.
         *
         * "Nada selecionado" num combo de código é ausência de valor, e é assim que os
         * `<select>` do sistema já o escreviam (`<option :value="null">Selecione</option>`).
         * String vazia enviada a um `int?` da API é recusada na desserialização — a requisição
         * inteira falha, não só o campo. `null` é aceito.
         *
         * Onde "nenhum" tem código próprio, a tela informa `valorVazio` e é ele que sai daqui.
         */
        if (texto === '') return this.valorVazio as string | number | null;

        if (!this.rowValue || !this.array?.length) return texto;

        const item = this.array.find((linha) => String(linha?.[this.rowValue]) === texto);

        return item === undefined ? texto : item[this.rowValue];
      },
    },
  });
</script>

<style scoped>
  .dropdown-consulte {
    width: 100%;
  }
</style>
